package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Point
import kotlin.math.hypot
import kotlinx.browser.window
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.EventTarget
import org.w3c.dom.events.KeyboardEvent

/** All browser listeners and timers belong to the attachment and are released together on detach. */
internal class StageInput(private val controller:StageController,private val host:HTMLElement,private val canvas:HTMLCanvasElement) {
    private val listeners=mutableListOf<Triple<EventTarget,String,EventListener>>()
    private val touches=mutableMapOf<Int,Point>()
    private var left=0.0
    private var top=0.0
    private var pinch:Triple<StageViewport,Point,Double>?=null
    private var wheelTimer:Int?=null
    private var lastZoomPublish=0.0
    val listenerCount:Int get()=listeners.size
    fun measure(){val rect=host.getBoundingClientRect();left=rect.left;top=rect.top}
    private fun point(event:dynamic):Point=Point((event.clientX as Number).toDouble()-left,(event.clientY as Number).toDouble()-top)
    private fun listen(target:EventTarget,name:String,passive:Boolean=true,body:(Event)->Unit){
        val listener=EventListener(body);val options:dynamic=js("({})");options.passive=passive
        target.asDynamic().addEventListener(name,listener,options);listeners+=Triple(target,name,listener)
    }
    fun attach(){
        measure()
        listen(canvas,"pointerdown",false){event->
            val e=event.asDynamic();val p=point(e);val touch=e.pointerType=="touch";val id=(e.pointerId as Number).toInt()
            if (e.isTrusted as Boolean) canvas.asDynamic().setPointerCapture(id)
            host.focus();event.preventDefault()
            if(touch){
                touches[id]=p
                if(touches.size==2){controller.pointerUp(true,false);val points=touches.values.toList()
                    pinch=Triple(controller.viewport,Point((points[0].x+points[1].x)/2,(points[0].y+points[1].y)/2),hypot(points[1].x-points[0].x,points[1].y-points[0].y));return@listen}
            }
            controller.pointerDown(p,e.shiftKey as Boolean,(e.ctrlKey as Boolean)||(e.metaKey as Boolean),
                (e.button as Number).toInt()==1||controller.temporaryHand||controller.uiState.tool==StudioActionType.HAND_TOOL,touch)
        }
        listen(canvas,"pointermove",false){event->
            val e=event.asDynamic();val p=point(e);val id=(e.pointerId as Number).toInt()
            if(id in touches)touches[id]=p
            val start=pinch
            if(start!=null && touches.size>=2){
                val points=touches.values.take(2);val center=Point((points[0].x+points[1].x)/2,(points[0].y+points[1].y)/2)
                val distance=hypot(points[1].x-points[0].x,points[1].y-points[0].y)
                if(start.third>0)controller.viewport=start.first.zoomAbout(start.second,(start.first.zoom*distance/start.third).coerceIn(.05,8.0))
                    .panBy(Point(center.x-start.second.x,center.y-start.second.y))
                controller.redraw();event.preventDefault()
            }else controller.pointerMove(p,e.shiftKey as Boolean,e.altKey as Boolean,(e.ctrlKey as Boolean)||(e.metaKey as Boolean))
        }
        fun release(event:Event,cancelled:Boolean){
            val e=event.asDynamic();val id=(e.pointerId as Number).toInt();touches.remove(id)
            if(pinch!=null){pinch=null;touches.clear();controller.publish()}else controller.pointerUp(cancelled,e.shiftKey as? Boolean ?: false)
            if(canvas.asDynamic().hasPointerCapture(id) as Boolean)canvas.asDynamic().releasePointerCapture(id)
        }
        listen(canvas,"pointerup"){release(it,false)}
        listen(canvas,"pointercancel"){release(it,true)}
        listen(canvas,"lostpointercapture"){controller.pointerUp(true,false)}
        listen(canvas,"dblclick",false){event->
            val p=controller.viewport.toDocument(point(event.asDynamic()))
            if(controller.uiState.selected.singleOrNull() is GroupLayer)controller.selection.enter(controller.uiState.page)
            controller.selection.click(controller.uiState.page.copy(layers=controller.uiState.layers),p,EngineEditorGeometry,deep=true)
            controller.publish();controller.dispatch(StudioIntent.Action(StudioAction(StudioActionType.ENTER)));event.preventDefault()
        }
        listen(canvas,"wheel",false){event->
            val e=event.asDynamic();event.preventDefault()
            val factor=when((e.deltaMode as Number).toInt()){1->16.0;2->controller.viewport.height;else->1.0}
            val dx=(e.deltaX as Number).toDouble()*factor;val dy=(e.deltaY as Number).toDouble()*factor
            if ((e.ctrlKey as Boolean)||(e.metaKey as Boolean)) {
                if (controller.rasterZoom == null) controller.rasterZoom = controller.viewport.zoom * controller.viewport.pixelRatio
                controller.viewport=controller.viewport.wheelZoom(point(e),dy)
            }
            else controller.viewport=controller.viewport.panBy(if(e.shiftKey as Boolean)Point(-dy,-dx)else Point(-dx,-dy))
            controller.redraw()
            val time=window.performance.now()
            if(time-lastZoomPublish>=100){lastZoomPublish=time;controller.publish()}
            wheelTimer?.let{window.clearTimeout(it)};wheelTimer=window.setTimeout({wheelTimer=null;controller.rasterZoom=null;controller.redraw();controller.publish()},120)
        }
        var gestureStart:StageViewport?=null
        listen(canvas,"gesturestart",false){event->event.preventDefault();gestureStart=controller.viewport}
        listen(canvas,"gesturechange",false){event->
            event.preventDefault();val start=gestureStart ?: return@listen
            val scale=(event.asDynamic().scale as Number).toDouble()
            controller.viewport=start.zoomAbout(point(event.asDynamic()),(start.zoom*scale).coerceIn(.05,8.0));controller.redraw()
        }
        listen(canvas,"gestureend",false){event->event.preventDefault();gestureStart=null;controller.publish()}
        listen(host,"keydown",false){event->
            val e=event as KeyboardEvent
            if(e.isComposing||(e.target as? HTMLElement)?.closest("input,textarea,select,[contenteditable='true']")!=null)return@listen
            if(e.code=="Space"&&!e.ctrlKey&&!e.metaKey&&!e.altKey){controller.temporaryHand=true;e.preventDefault()}
            if(e.key=="Escape"&&controller.gesture!=null){controller.pointerUp(true,false);e.preventDefault()}
        }
        listen(host,"focus"){host.style.outline="2px solid var(--studio-accent)"}
        listen(host,"blur"){host.style.outline=""}
        listen(host,"keyup"){event->if((event as KeyboardEvent).code=="Space")controller.temporaryHand=false}
        listen(window,"blur"){controller.temporaryHand=false;touches.clear();pinch=null;controller.pointerUp(true,false)}
        listen(window,"resize"){measure()}
        // Scroll position is measured only on scroll, never on a pointermove.
        listen(window,"scroll"){measure()}
    }
    fun detach(){
        listeners.forEach{(target,name,listener)->target.removeEventListener(name,listener)};listeners.clear()
        wheelTimer?.let{window.clearTimeout(it)};wheelTimer=null;touches.clear();pinch=null
    }
}
